/**
 * Lexicon — Language Intelligence Types
 *
 * NLP meta-model profiling from meeting transcripts.
 * Tracks deletion, distortion, and generalization patterns
 * per speaker, building a "baseball card" communication profile.
 */

// ── NLP Meta-Model ────────────────────────────────────────────────────────────

export type NlpPatternCategory = 'deletion' | 'distortion' | 'generalization';

export interface DetectedPattern {
  category: NlpPatternCategory;
  type: string;         // e.g. "Mind reading", "Universal quantifier"
  count: number;
  examples: string[];   // verbatim quotes from the transcript
}

// ── Transcript ────────────────────────────────────────────────────────────────

export type AnalysisStatus = 'pending' | 'processing' | 'complete' | 'failed';

export interface LexiconTranscript {
  id: string;
  workspaceId: string;
  title: string;
  meetingDate: string | null;   // ISO date string
  rawContent: string;
  speakers: string[];
  analysisStatus: AnalysisStatus;
  analysisError: string | null;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateTranscriptInput {
  workspaceId: string;
  title: string;
  meetingDate?: string | null;
  rawContent: string;
  createdBy?: string;
}

// ── Speaker Analysis (per-transcript) ────────────────────────────────────────

export interface SpeakerAnalysis {
  id: string;
  transcriptId: string;
  workspaceId: string;
  userId: string | null;
  speakerName: string;

  // NLP meta-model scores 0–100
  specificityScore: number | null;    // inverse of deletion density
  clarityScore: number | null;        // inverse of distortion density
  flexibilityScore: number | null;    // inverse of generalization rigidity

  // Communication style scores 0–100
  agencyScore: number | null;         // ownership / internal locus of control
  momentumScore: number | null;       // action + commitment density
  strategicIndex: number | null;      // big-picture vs. operational orientation

  // Behavioral metrics
  actionCommitmentRate: number | null; // % of turns with concrete commitment
  questionRate: number | null;         // % of turns containing questions
  hedgingFrequency: number | null;     // frequency of hedge words

  // Content
  topTopics: string[];
  keyPhrases: string[];
  notableQuotes: string[];

  // Detected NLP patterns
  detectedPatterns: DetectedPattern[];

  // Coaching output
  strengths: string[];
  blindSpots: string[];
  recommendations: string[];

  rawAnalysis: Record<string, unknown> | null;
  wordCount: number | null;
  turnCount: number | null;
  createdAt: string;
}

// ── Speaker Profile (aggregated across transcripts) ───────────────────────────

export type CommunicationArchetype =
  | 'Strategist'    // high strategic, moderate everything else
  | 'Executor'      // high momentum + specificity, lower strategic
  | 'Analyst'       // high clarity + specificity, methodical
  | 'Connector'     // balanced, high question rate, stakeholder bridging
  | 'Visionary'     // high strategic + flexibility, low specificity
  | 'Challenger'    // high flexibility + clarity, challenges assumptions
  | 'Operator'      // high agency + momentum, execution-first
  | 'Undefined';

export interface SpeakerProfile {
  id: string;
  workspaceId: string;
  userId: string | null;
  speakerName: string;

  // Aggregated scores
  avgSpecificity: number | null;
  avgClarity: number | null;
  avgFlexibility: number | null;
  avgAgency: number | null;
  avgMomentum: number | null;
  avgStrategicIndex: number | null;
  avgActionCommitmentRate: number | null;

  // Derived
  archetype: CommunicationArchetype | null;
  archetypeDescription: string | null;

  // Coaching
  consistentStrengths: string[];
  persistentBlindSpots: string[];
  growthAreas: string[];

  topPatterns: DetectedPattern[];
  topTopics: string[];

  transcriptCount: number;
  totalWordCount: number;
  updatedAt: string;
}

// ── Analysis LLM response shape ───────────────────────────────────────────────

export interface LlmAnalysisOutput {
  speakers: LlmSpeakerResult[];
}

export interface LlmSpeakerResult {
  speakerName: string;
  scores: {
    specificity: number;
    clarity: number;
    flexibility: number;
    agency: number;
    momentum: number;
    strategicIndex: number;
  };
  actionCommitmentRate: number;
  questionRate: number;
  hedgingFrequency: number;
  topTopics: string[];
  keyPhrases: string[];
  notableQuotes: string[];
  detectedPatterns: DetectedPattern[];
  strengths: string[];
  blindSpots: string[];
  recommendations: string[];
  wordCount: number;
  turnCount: number;
}

// ── UI helpers ────────────────────────────────────────────────────────────────

export const DIMENSION_LABELS: Record<string, string> = {
  avgSpecificity:   'Specificity',
  avgClarity:       'Clarity',
  avgFlexibility:   'Flexibility',
  avgAgency:        'Agency',
  avgMomentum:      'Momentum',
  avgStrategicIndex: 'Strategic Index',
};

export const DIMENSION_ABBREVIATIONS: Record<string, string> = {
  avgSpecificity:   'SPE',
  avgClarity:       'CLR',
  avgFlexibility:   'FLX',
  avgAgency:        'AGN',
  avgMomentum:      'MOM',
  avgStrategicIndex: 'STR',
};

export const NLP_PATTERN_TYPES: Record<NlpPatternCategory, string[]> = {
  deletion: [
    'Simple deletion',
    'Comparative deletion',
    'Lack of referential index',
    'Unspecified verb',
    'Nominalization',
  ],
  distortion: [
    'Mind reading',
    'Lost performative',
    'Cause and effect',
    'Complex equivalence',
    'Presupposition',
  ],
  generalization: [
    'Universal quantifier',
    'Modal operator (necessity)',
    'Modal operator (possibility)',
    'Model operator (impossibility)',
  ],
};

/** Derive a communication archetype from profile scores */
export function deriveArchetype(profile: Pick<SpeakerProfile,
  'avgSpecificity' | 'avgClarity' | 'avgFlexibility' |
  'avgAgency' | 'avgMomentum' | 'avgStrategicIndex'
>): CommunicationArchetype {
  const s = profile.avgSpecificity ?? 50;
  const c = profile.avgClarity ?? 50;
  const f = profile.avgFlexibility ?? 50;
  const a = profile.avgAgency ?? 50;
  const m = profile.avgMomentum ?? 50;
  const st = profile.avgStrategicIndex ?? 50;

  if (st >= 75 && m < 60) return 'Visionary';
  if (st >= 70 && m >= 55) return 'Strategist';
  if (m >= 75 && s >= 75) return 'Executor';
  if (s >= 75 && c >= 75 && st < 60) return 'Analyst';
  if (f >= 70 && c >= 70 && a >= 65) return 'Challenger';
  if (a >= 75 && m >= 65) return 'Operator';
  if (c >= 65 && f >= 65 && s >= 60) return 'Connector';

  return 'Undefined';
}

export const ARCHETYPE_DESCRIPTIONS: Record<CommunicationArchetype, string> = {
  Strategist: 'Frames problems at the organizational level. Connects initiatives to outcomes. May leave ownership and timelines underspecified.',
  Executor: 'Drives concrete action. High commitment density. Can become rigid when alternatives challenge the plan.',
  Analyst: 'Precise, data-grounded language. Low distortion patterns. May get lost in detail at the expense of narrative.',
  Connector: 'Bridges perspectives and stakeholders. Asks more questions than average. Watch for speaking on behalf of others.',
  Visionary: 'Paints compelling futures. High strategic abstraction. Benefits from a grounding partner who tracks specifics.',
  Challenger: 'Questions assumptions. Surfaces alternatives naturally. Can be perceived as resistant when not grounded in action.',
  Operator: 'Gets things done. Strong ownership language. Risk of narrow framing; may miss emergent strategic signals.',
  Undefined: 'Insufficient data to determine a dominant archetype.',
};
